import type { ComponentType, ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { getHostAudience, LazyBoundary, lazyPage, NotFound } from '@l2u/web-shared';
import {
  familyBase,
  isAudienceHost,
  LEGACY_PREFIX,
  legacyForwardPath,
  notFoundLinks,
  type COMPANY_SEGMENTS,
  type DRIVER_SEGMENTS,
  type PortalFamily,
} from './lib/audienceRoutes.ts';
import { CompanyLanding } from './auth/CompanyLanding.tsx';
import { CompanyCallback } from './auth/CompanyCallback.tsx';
import { CompanyExpired } from './auth/CompanyExpired.tsx';
import { DriverExpired } from './auth/DriverExpired.tsx';
import { CompanyLocked } from './auth/CompanyLocked.tsx';
import { DriverLocked } from './auth/DriverLocked.tsx';
import { CompanyLogin, DriverLogin } from './auth/LoginCodeRequest.tsx';
import { DriverLanding } from './auth/DriverLanding.tsx';
import { DriverCallback } from './auth/DriverCallback.tsx';
import { CompanyShell } from './company/CompanyShell.tsx';
import { DriverShell } from './driver/DriverShell.tsx';

// Route-level code splitting. The sign-in pages, callbacks and the two shells
// stay in the entry chunk — they are what a cold visit paints first — and every
// page behind a session (plus the token-only visitor and preview pages) is its
// own chunk, fetched on first navigation. The questionnaire wizard
// (company/Questionnaire.tsx) is shared by the company, driver, asset and
// preview pages, so the bundler gives it a chunk of its own that all four reuse.
// lazyPage retries a failed import and reloads once on a stale deploy; see
// packages/web-shared/src/lib/lazyPage.tsx.
const CompanyInformation = lazyPage(() =>
  import('./company/CompanyInfo.tsx').then((m) => m.CompanyInformation),
);
const CompanyDrivers = lazyPage(() =>
  import('./company/Drivers.tsx').then((m) => m.CompanyDrivers),
);
const CompanyDriverDetail = lazyPage(() =>
  import('./company/DriverDetail.tsx').then((m) => m.CompanyDriverDetail),
);
const Questionnaire = lazyPage(() =>
  import('./company/Questionnaire.tsx').then((m) => m.Questionnaire),
);
const CompanyTraining = lazyPage(() =>
  import('./company/CompanyTraining.tsx').then((m) => m.CompanyTraining),
);
const MyDocuments = lazyPage(() => import('./company/MyDocuments.tsx').then((m) => m.MyDocuments));
const CompanyLibrary = lazyPage(() =>
  import('./company/Library.tsx').then((m) => m.CompanyLibrary),
);
const CompanyAssets = lazyPage(() => import('./company/Assets.tsx').then((m) => m.CompanyAssets));
const CompanyDashboard = lazyPage(() =>
  import('./company/Dashboard.tsx').then((m) => m.CompanyDashboard),
);
const CompanyAssetDetail = lazyPage(() =>
  import('./company/AssetDetail.tsx').then((m) => m.CompanyAssetDetail),
);
const CompanyAssetQuestionnaire = lazyPage(() =>
  import('./company/AssetQuestionnaire.tsx').then((m) => m.CompanyAssetQuestionnaire),
);
const CompanyCorrectiveActions = lazyPage(() =>
  import('./company/CorrectiveActions.tsx').then((m) => m.CompanyCorrectiveActions),
);
const CompanyFileNotes = lazyPage(() =>
  import('./company/CompanyFileNotes.tsx').then((m) => m.CompanyFileNotes),
);
const CompanyFileNote = lazyPage(() =>
  import('./company/CompanyFileNotes.tsx').then((m) => m.CompanyFileNote),
);
const DriverInfo = lazyPage(() => import('./driver/DriverInfo.tsx').then((m) => m.DriverInfo));
const DriverQuestionnaire = lazyPage(() =>
  import('./driver/DriverQuestionnaire.tsx').then((m) => m.DriverQuestionnaire),
);
const DriverTraining = lazyPage(() =>
  import('./driver/DriverTraining.tsx').then((m) => m.DriverTraining),
);
const DriverLibrary = lazyPage(() =>
  import('./driver/DriverLibrary.tsx').then((m) => m.DriverLibrary),
);
const DriverFileNotes = lazyPage(() =>
  import('./driver/DriverFileNotes.tsx').then((m) => m.DriverFileNotes),
);
const DriverFileNote = lazyPage(() =>
  import('./driver/DriverFileNotes.tsx').then((m) => m.DriverFileNote),
);
const VisitorRegister = lazyPage(() =>
  import('./visitor/VisitorRegister.tsx').then((m) => m.VisitorRegister),
);
const VisitorProof = lazyPage(() =>
  import('./visitor/VisitorProof.tsx').then((m) => m.VisitorProof),
);
const QuestionnairePreview = lazyPage(() =>
  import('./preview/QuestionnairePreview.tsx').then((m) => m.QuestionnairePreview),
);

/**
 * A lazy page in its own spinner/error boundary. Keyed by route so a failure on
 * one page does not stick to the next one rendered in the same outlet slot.
 */
function lazyRoute(key: string, Page: ComponentType): ReactElement {
  return (
    <LazyBoundary key={key}>
      <Page />
    </LazyBoundary>
  );
}

// The shell pages of each family, keyed by their top-level segment. Typed
// against the segment lists in lib/audienceRoutes.ts, so a page added here but
// not declared there — which would make the legacy forward treat /c/<page> as
// an invitation token — is a compile error, and so is the reverse.
type Pages<Segments extends readonly string[]> = Record<
  Exclude<Segments[number], 'login' | 'callback' | 'expired' | 'locked' | 'invite' | 'preview'>,
  ReactElement
>;

const COMPANY_PAGES: Pages<typeof COMPANY_SEGMENTS> = {
  dashboard: lazyRoute('CompanyDashboard', CompanyDashboard),
  info: lazyRoute('CompanyInformation', CompanyInformation),
  questionnaire: lazyRoute('Questionnaire', Questionnaire),
  training: lazyRoute('CompanyTraining', CompanyTraining),
  drivers: lazyRoute('CompanyDrivers', CompanyDrivers),
  documents: lazyRoute('MyDocuments', MyDocuments),
  assets: lazyRoute('CompanyAssets', CompanyAssets),
  'corrective-actions': lazyRoute('CompanyCorrectiveActions', CompanyCorrectiveActions),
  'file-notes': lazyRoute('CompanyFileNotes', CompanyFileNotes),
  library: lazyRoute('CompanyLibrary', CompanyLibrary),
};

const DRIVER_PAGES: Pages<typeof DRIVER_SEGMENTS> = {
  details: lazyRoute('DriverInfo', DriverInfo),
  questionnaire: lazyRoute('DriverQuestionnaire', DriverQuestionnaire),
  training: lazyRoute('DriverTraining', DriverTraining),
  library: lazyRoute('DriverLibrary', DriverLibrary),
  'file-notes': lazyRoute('DriverFileNotes', DriverFileNotes),
};

// `root` is where a family is mounted: '' on its own host, the legacy prefix on
// a host that serves every family. `rootMounted` picks the invitation shape:
// /invite/<token> at the root, where a bare /<token> would swallow every
// unknown path into a failed redemption, and the legacy /c/<token> under the
// prefix, which is what the API still emits in legacy mode.
function companyRoutes(root: string, rootMounted: boolean) {
  return (
    <>
      {/* Self-service sign-in. Declared BEFORE the /c/:token landing: a dynamic
          segment would otherwise match the literal path and try to redeem
          "login" as an invitation token. */}
      <Route path={`${root}/login`} element={<CompanyLogin />} />
      {/* Admin questionnaire preview (spec 2026-09-30): token-only, outside the
          shell, so no session is needed. */}
      <Route
        path={`${root}/preview/:token`}
        element={lazyRoute('QuestionnairePreview', QuestionnairePreview)}
      />
      <Route path={`${root}/${rootMounted ? 'invite/' : ''}:token`} element={<CompanyLanding />} />
      <Route path={`${root}/callback`} element={<CompanyCallback />} />
      <Route path={`${root}/expired`} element={<CompanyExpired />} />
      <Route path={`${root}/locked`} element={<CompanyLocked />} />
      <Route path={root || '/'} element={<CompanyShell />}>
        <Route index element={<Navigate to={`${root}/dashboard`} replace />} />
        {Object.entries(COMPANY_PAGES).map(([path, element]) => (
          <Route key={path} path={path} element={element} />
        ))}
        <Route path="drivers/:id" element={lazyRoute('CompanyDriverDetail', CompanyDriverDetail)} />
        <Route path="assets/:id" element={lazyRoute('CompanyAssetDetail', CompanyAssetDetail)} />
        <Route
          path="assets/:id/questionnaires/:questionnaireId"
          element={lazyRoute('CompanyAssetQuestionnaire', CompanyAssetQuestionnaire)}
        />
        {/* One note; the file-note email links here. */}
        <Route path="file-notes/:id" element={lazyRoute('CompanyFileNote', CompanyFileNote)} />
      </Route>
    </>
  );
}

function driverRoutes(root: string, rootMounted: boolean) {
  return (
    <>
      <Route path={`${root}/login`} element={<DriverLogin />} />
      {/* Admin questionnaire preview (spec 2026-09-30): token-only, outside the
          shell, so no session is needed. */}
      <Route
        path={`${root}/preview/:token`}
        element={lazyRoute('QuestionnairePreview', QuestionnairePreview)}
      />
      <Route path={`${root}/${rootMounted ? 'invite/' : ''}:token`} element={<DriverLanding />} />
      <Route path={`${root}/callback`} element={<DriverCallback />} />
      <Route path={`${root}/expired`} element={<DriverExpired />} />
      <Route path={`${root}/locked`} element={<DriverLocked />} />
      <Route path={root || '/'} element={<DriverShell />}>
        <Route index element={<Navigate to={`${root}/details`} replace />} />
        {Object.entries(DRIVER_PAGES).map(([path, element]) => (
          <Route key={path} path={path} element={element} />
        ))}
        {/* One note; the file-note email links here. */}
        <Route path="file-notes/:id" element={lazyRoute('DriverFileNote', DriverFileNote)} />
      </Route>
    </>
  );
}

// Visitor portal (public, no auth). The landing is the family root — `/` on
// the visitor host, `/visit` on the shared one — and the proof page is
// v/:visitorId beneath it. The PROOF half is emitted in two places that must
// agree with this: the api's lib/tenantUrl.ts (visitorProofUrl, for the SMS
// link) and this SPA's visitorProofPath (lib/workspaceHost.ts, for the
// post-register navigate). The LANDING half is assumption [A4]: nothing in
// code emits it — the QR code is printed outside this repo.
function visitorRoutes(root: string) {
  return (
    <>
      <Route path={root || '/'} element={lazyRoute('VisitorRegister', VisitorRegister)} />
      <Route path={`${root}/v/:visitorId`} element={lazyRoute('VisitorProof', VisitorProof)} />
    </>
  );
}

/**
 * A legacy-prefixed path on its family's own host, sent to the short form.
 * `search` and `hash` ride along: the magic-link callback carries its session
 * in the fragment, and an email already sent cannot be re-issued.
 */
function LegacyForward({ family }: { family: PortalFamily }) {
  const { pathname, search, hash } = useLocation();
  const to = legacyForwardPath(family, pathname) ?? '/';
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

export function App() {
  const audience = getHostAudience();

  // On a per-audience host ONLY that family is mounted, at the root. The other
  // two families' paths are not routes here at all, so /d/login on the company
  // host falls to NotFound — the answer the #98 gate gave, now by construction
  // instead of by a check that has to stay above <Routes>. getHostAudience() is
  // safe this early because main.tsx awaits the boot gate before the first
  // render.
  if (isAudienceHost(audience)) {
    return (
      <Routes>
        {audience === 'company' && companyRoutes('', true)}
        {audience === 'driver' && driverRoutes('', true)}
        {audience === 'visitor' && visitorRoutes('')}
        {/* A splat also matches the bare prefix, so /c and /visit land here too. */}
        <Route
          path={`${LEGACY_PREFIX[audience]}/*`}
          element={<LegacyForward family={audience} />}
        />
        <Route path="*" element={<NotFound links={notFoundLinks(audience)} />} />
      </Routes>
    );
  }

  // The legacy shared base, local dev and the platform host: one origin serves
  // all three families, so each keeps its prefix. Read through familyBase, the
  // same function every link goes through, so routes and links cannot disagree.
  const c = familyBase('company', audience);
  const d = familyBase('driver', audience);
  const v = familyBase('visitor', audience);
  return (
    <Routes>
      {companyRoutes(c, false)}
      {driverRoutes(d, false)}
      {visitorRoutes(v)}
      {/* The legacy shape for the shared host, where neither the hostname nor
          the Origin identifies a tenant, so the link carries the workspace.
          Deleted by the Prod deletion PR, once every environment is on
          per-audience hosts. Segment counts keep these clear of the routes
          above: /c/login/:s is 3 and /c/:token 2; /visit/:s is 2 and
          /visit/:s/v/:id 4. */}
      <Route path={`${c}/login/:tenantSlug`} element={<CompanyLogin />} />
      <Route path={`${d}/login/:tenantSlug`} element={<DriverLogin />} />
      <Route path={`${v}/:tenantSlug`} element={lazyRoute('VisitorRegister', VisitorRegister)} />
      <Route
        path={`${v}/:tenantSlug/v/:visitorId`}
        element={lazyRoute('VisitorProof', VisitorProof)}
      />

      <Route path="*" element={<NotFound links={notFoundLinks(audience)} />} />
    </Routes>
  );
}
